import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { EditPermissionDialogProps } from '../types';
import { entityConfig, permissionConfig } from '../constants/entityConfig';
import { getEntityName } from '../utils/entityUtils';
import { PermissionLevel } from '../../../../../generated/data-contracts';

export const EditPermissionDialog: React.FC<EditPermissionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  permission,
  loading = false,
}) => {
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>(
    PermissionLevel.READ
  );

  useEffect(() => {
    if (permission) {
      setPermissionLevel(permission.permissionLevel);
    }
  }, [permission]);

  if (!permission) return null;

  const config = entityConfig[permission.entityType];
  const currentPermissionInfo = permissionConfig[permission.permissionLevel];

  const handleConfirm = () => {
    onConfirm({
      ...permission,
      permissionLevel,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              width: 48,
              height: 48,
            }}
          >
            <EditIcon />
          </Avatar>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
              Editar Permiso
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              Modifica el nivel de acceso para esta entidad.
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.default',
            border: `1px solid ${config?.color || '#ccc'}20`,
            borderRadius: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Avatar
              sx={{
                bgcolor: config?.bgColor || '#f5f5f5',
                color: config?.color || '#666',
                width: 32,
                height: 32,
              }}
            >
              {config?.icon}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                {getEntityName(permission)}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {config?.name || permission.entityType}
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={currentPermissionInfo?.icon}
            label={currentPermissionInfo?.label || permission.permissionLevel}
            size='small'
            sx={{
              bgcolor: currentPermissionInfo?.bgColor,
              color: currentPermissionInfo?.color,
              fontWeight: 'bold',
            }}
          />
        </Box>

        <FormControl fullWidth>
          <InputLabel id='permission-level-label'>Nivel de Permiso</InputLabel>
          <Select
            labelId='permission-level-label'
            value={permissionLevel}
            label='Nivel de Permiso'
            onChange={e =>
              setPermissionLevel(e.target.value as PermissionLevel)
            }
            disabled={loading}
          >
            {Object.values(PermissionLevel).map(level => {
              const info = permissionConfig[level as PermissionLevel];
              return (
                <MenuItem key={level} value={level}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {info?.icon &&
                      React.cloneElement(info.icon, {
                        sx: { color: info.color },
                      })}
                    <Typography
                      sx={{ color: info?.color, fontWeight: 'medium' }}
                    >
                      {info?.label || level}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant='outlined'
          size='large'
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant='contained'
          color='primary'
          size='large'
          startIcon={
            loading ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <EditIcon />
            )
          }
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
